import { Item } from '@/interfaces/cash-register/Item'
import { PaymentType } from '@invo.space/shared'
import { Sku } from '@/interfaces/catalog/Sku'

export const paymentTypeLabel: Record<string, string> = {
  [PaymentType.Cash]: 'Hotově',
  [PaymentType.Card]: 'Kartou',
  [PaymentType.Qr]: 'QR kód',
}

const qtyFormat = new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 3 })

export const formatQty = (value: number) => qtyFormat.format(value)

/** Cena řádku po slevě v korunách. */
export const lineTotal = (item: Item) => item.amount * item.unitPrice - item.getSaleDiscountAmount()

export interface TicketTotals {
  subtotal: number
  discount: number
  total: number
  taxes: { rate: number; tax: number }[]
}

/** Souhrn účtenky. Ceny jsou s DPH, daň počítáme shora: základ × r / (100 + r). */
export const ticketTotals = (items: Item[]): TicketTotals => {
  let subtotal = 0
  let discount = 0
  const byRate = new Map<number, number>()

  for (const item of items) {
    subtotal += item.amount * item.unitPrice
    discount += item.getSaleDiscountAmount()
    if (item.taxRate) byRate.set(item.taxRate, (byRate.get(item.taxRate) ?? 0) + lineTotal(item))
  }

  const taxes = Array.from(byRate.entries())
    .sort(([a], [b]) => b - a)
    .map(([rate, gross]) => ({ rate, tax: (gross * rate) / (100 + rate) }))

  return { subtotal, discount, total: subtotal - discount, taxes }
}

/** Pro hledání bez ohledu na diakritiku a velikost písmen. */
export const normalize = (value: string | null | undefined) =>
  (value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()

/** Název varianty bez opakování názvu produktu ("Káva - 250 g" -> "250 g"). */
export const variantLabel = (sku: Pick<Sku, 'name'>, productName: string) => {
  if (sku.name && sku.name !== productName && sku.name.startsWith(productName)) {
    const rest = sku.name.slice(productName.length).replace(/^[\s,\-:]+/, '').trim()
    if (rest) return rest
  }
  return sku.name || productName
}

/** Popisek řádku účtenky: "Kolumbie Decaf, 250 g" místo opakování názvu produktu. */
export const itemLabel = (item: Item) => {
  if (!item.productName) return item.description
  if (!item.skuName || item.skuName === item.productName) return item.productName
  return `${item.productName}, ${variantLabel({ name: item.skuName }, item.productName)}`
}

/** České skloňování podle počtu: 1 položka, 2 položky, 5 položek. */
export const plural = (count: number, one: string, few: string, many: string) =>
  count === 1 ? one : count >= 2 && count <= 4 ? few : many

/** Jednotková cena varianty v korunách (API vrací haléře). */
export const skuUnitPrice = (sku: Sku): number =>
  sku.price?.price?.amount ? Number(sku.price.price.amount) / 100 : 0

export const skuTaxRate = (sku: Sku): number | undefined => {
  const percentage = sku.price?.taxRate?.percentage
  return percentage != null ? Number(percentage) : undefined
}
